import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthLayout } from "../../components/AuthLayout";
import { useAuth } from "../../lib/auth-context";
import { ApiError } from "../../lib/api";

export function SignUp() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ack, setAck] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const strength = Math.min(4, Math.floor(password.length / 3));

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!ack) {
      setError("Please confirm you're of legal drinking age where you live.");
      return;
    }
    setBusy(true);
    try {
      await signup(name, email, password, ack);
      navigate("/onboarding/craft");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      posterTitle="Write down what you did, once."
      posterBody="Every batch you log makes the next one better — and gives the next person a real number instead of a guess."
      step={1}
    >
      <span className="eyebrow">Step 1 of 4 · about a minute</span>
      <h2 style={{ margin: "12px 0 0", fontSize: 38, lineHeight: 1.05, letterSpacing: "-0.028em", marginLeft: "-0.055em" }}>
        Make an account
      </h2>
      <p className="muted" style={{ margin: "14px 0 30px", fontSize: 14.5, lineHeight: 1.6 }}>
        Your name shows on recipes you publish. Everything else stays yours.
      </p>

      <form onSubmit={onSubmit}>
        <div className="field" style={{ marginBottom: 16 }}>
          <label>Name or handle</label>
          <input className="input" style={{ minHeight: 44 }} placeholder="Jules R." required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field" style={{ marginBottom: 16 }}>
          <label>Email</label>
          <input
            className="input"
            style={{ minHeight: 44 }}
            type="email"
            placeholder="you@example.com"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field" style={{ marginBottom: 8 }}>
          <label>Password</label>
          <input
            className="input"
            style={{ minHeight: 44 }}
            type="password"
            placeholder="At least 10 characters"
            required
            minLength={10}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div style={{ display: "flex", gap: 2, marginBottom: 8 }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{ height: 5, flex: 1, background: i < strength ? "var(--color-accent)" : "var(--color-neutral-300)" }} />
          ))}
        </div>
        <p className="muted-strong" style={{ margin: "0 0 24px", fontSize: 12.5 }}>
          {password.length < 10 ? "Ten characters minimum." : "Strong enough. Three words and a number beats a symbol soup."}
        </p>
        <label className="radio" style={{ alignItems: "flex-start", marginBottom: 24 }}>
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
          <span className="dot" style={{ marginTop: 2 }} />
          <span style={{ fontSize: 13.5, lineHeight: 1.55 }}>
            I'm of legal drinking age where I live, and I understand Alembic covers infusions and blending — not distilling.
          </span>
        </label>
        {error && <p className="error-text">{error}</p>}
        <button className="btn btn-primary btn-block" style={{ minHeight: 48 }} disabled={busy} type="submit">
          {busy ? "Creating your account…" : "Continue"}
        </button>
      </form>

      <p className="muted" style={{ margin: "20px 0 0", fontSize: 13.5 }}>
        Already have one? <Link to="/sign-in">Sign in</Link>.
      </p>
    </AuthLayout>
  );
}
